import { Alert, Box, Button, Card, CardContent, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import SimpleBackdrop from "../../../modules/common/shared/SimpleBackdrop";
import { UserRole } from "../../../modules/common/shared/types";
import { getLoggedInUserRole } from "../../../modules/common/user/UserService";
import { FeeDue } from "../../feeDue/types";
import { useFeePayment } from "../hooks/useFeePayment";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import PageTopBar from "../../../components/common/PageTopBar";
import { SubmitButton } from "../../../components/common/form/SubmitButton";

export const FeeFillingForm = () => {
  const role = getLoggedInUserRole();

  const location = useLocation();
  const navigate = useNavigate();
  const feeDue: FeeDue = location.state?.feeDue;

  const {
    loading,
    error,
    balanceAmount,
    transactionIdRequired,
    setTransactionIdRequired,
    currentYearDiscountDisabled,
    updateCurrentYDiscountDisability,
    initiatePayment,
    submitting,
    transactionIdRef,
    previousYDiscountRef,
    currentYearDiscountRef,
    amountPayingRef,
    paymentButtonRef,
  } = useFeePayment({ feeDue });

  const previousYearDuesGThanZero = feeDue?.previousTotalDue > 0;

  if (!feeDue || feeDue === undefined) {
    return <Navigate to="/app/fees/outstanding" replace />;
  }

  const updateToCaps = (e: React.FormEvent<HTMLInputElement>) => {
    const input = transactionIdRef.current;
    if (!input) return false;
    input.value = input.value.toUpperCase();
    e.currentTarget.value = input.value;
  };

  // two columns from sm up, one on phones (styling only)
  const twoColumns = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 };
  const sectionLabel = { textTransform: "uppercase", letterSpacing: 0.3 } as const;
  // [Previous Year Discount] [Current Year Discount] [Transaction ID] on one row; the previous-year
  // discount shows only with previous dues, Transaction ID only for non-cash payments. A lone field
  // keeps half width, like Amount Paying.
  const rowColumns = [previousYearDuesGThanZero ? "1fr" : null, "1fr", transactionIdRequired ? "1.4fr" : null].filter(Boolean);
  const threeColumns = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: rowColumns.length === 1 ? "1fr 1fr" : rowColumns.join(" ") },
    gap: 2,
  };

  return (
    <Box sx={{ mt: 2, mr: 2 }}>
      <PageTopBar
        left={
          <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
            <Typography variant="h6" fontWeight={600}>
              Fee Payment
            </Typography>
            {/* students only: they're sent on to the payment gateway */}
            {role === UserRole.STUDENT && (
              <Typography variant="body2" color="text.secondary">
                Please enter the amount and click submit. You will then be redirected to the payment gateway to complete your transaction.
              </Typography>
            )}
          </Box>
        }
      />

      <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, maxWidth: 800, ml: { xs: 0, md: "100px" } }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Box
            component="form"
            onSubmit={initiatePayment}
            sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
          >
            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={twoColumns}>
            <TextField
              fullWidth
              label="Name of the Student"
              name="name"
              type="text"
              size="small"
              value={feeDue?.studentName ?? ""}
              slotProps={{ input: { readOnly: true } }}
            />

            <TextField
              fullWidth
              label="Balance Amount"
              name="balanceAmount"
              type="number"
              size="small"
              value={balanceAmount}
              helperText={
                role === UserRole.ADMIN && previousYearDuesGThanZero
                  ? `Previous Year Dues: ${feeDue.previousTotalDue}`
                  : ""
              }
              slotProps={{ input: { readOnly: true } }}
            />
            </Box>

            {role === UserRole.ADMIN ? (
              <>
                <Divider />
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={sectionLabel}>
                  Payment Details
                </Typography>

                <Box sx={twoColumns}>
                <DatePicker
                  label="Date Of Payment"
                  slotProps={{
                    textField: ({ isFieldRequired, ...params }) => ({
                      ...params,
                      name: "dateOfPayment",
                      size: "small",
                    }),
                  }}
                  sx={{ width: "100%" }}
                  format="DD-MM-YYYY"
                />

                <FormControl fullWidth size="small">
                  <InputLabel id="pt-select-label">Payment Type</InputLabel>
                  <Select
                    labelId="pt-select-label"
                    name="paymentType"
                    label="Payment Type"
                    defaultValue="CASH"
                    onChange={(e) => {
                      if (e.target.value === "CASH") {
                        if (transactionIdRef.current) transactionIdRef.current.value = "";
                        setTransactionIdRequired(false);
                      } else {
                        setTransactionIdRequired(true);
                      }
                    }}
                  >
                    {["CASH", "UPI", "BankTransfer", "CHEQUE"].map((pt) => (
                      <MenuItem value={pt} key={pt}>{pt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                </Box>

                {/* Transaction ID, Previous Year Discount (when there are previous dues), Current Year Discount: one row */}
                <Box sx={threeColumns}>
                {previousYearDuesGThanZero && (
                  <TextField
                    fullWidth
                    label="Previous Year Discount"
                    name="previousYDiscount"
                    type="number"
                    size="small"
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    inputRef={previousYDiscountRef}
                    onChange={(e) => updateCurrentYDiscountDisability(e, "PREVIOUS_YEARS_DUE_INPUT")}
                  />
                )}

                <TextField
                  fullWidth
                  label="Current Year Discount"
                  name="currentYDiscount"
                  type="number"
                  size="small"
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                  inputRef={currentYearDiscountRef}
                  disabled={currentYearDiscountDisabled}
                />

                {/* only for non-cash payments (UPI, bank transfer, cheque) */}
                {transactionIdRequired && (
                  <TextField
                    fullWidth
                    label="Transaction ID/Cheque No."
                    name="transactionId"
                    type="text"
                    size="small"
                    inputRef={transactionIdRef}
                    onInput={updateToCaps}
                    disabled={!transactionIdRequired}
                    required={transactionIdRequired}
                  />
                )}
                </Box>
              </>
            ) : null}

            <Divider />
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={sectionLabel}>
              Amount
            </Typography>
            <Box sx={twoColumns}>
            <TextField
              fullWidth
              label="Amount Paying"
              name="amountPaying"
              type="number"
              size="small"
              onWheel={(e) => (e.target as HTMLElement).blur()}
              slotProps={{ htmlInput: { max: balanceAmount } }}
              inputRef={amountPayingRef}
              onChange={(e) => updateCurrentYDiscountDisability(e, "AMOUNT_PAYING_INPUT")}
              required
            />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "row", gap: 1.5, alignItems: "center", mt: 1 }}>
              <SubmitButton submitting={submitting} color="primary" disabled={feeDue.paymentOrderInProgress} />
              <Button type="button" color="secondary" variant="outlined" onClick={
                () => navigate("/app/fees/outstanding", { state: { feeDue } })}>
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <SimpleBackdrop open={loading} onClose={() => { }} />
    </Box>
  );
};